import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, Smile, Phone, Video, MoreVertical, Shield, Clock, FileText, Calendar, CheckCircle, Lock, Star } from 'lucide-react';
import Button from '../../../components/ui/Button';

const SecureMessageThread = ({ 
  conversation, 
  currentUser, 
  encryptionLevel,
  messageExpiration,
  onStartBooking
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping] = useState(false); // suppression setter inutilisé
  const [selectedFile, setSelectedFile] = useState(null);
  const [_showBookingModal] = useState(false);
  const [_messageTimer] = useState(null);
  const [_encryptionStatus, setEncryptionStatus] = useState('active');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadMessages();
    setupMessageEncryption();
    scrollToBottom();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fonctions stables locales
  }, [conversation]);

  const loadMessages = async () => {
    // Mock secure messages with encryption indicators
    const mockMessages = [
      {
        id: 1,
        content: "Bonjour ! Je suis intéressé par une collaboration pour un événement en octobre.",
        sender_id: conversation?.participants?.[0]?.user_profiles?.id,
        created_at: new Date(Date.now() - 2 * 60 * 60 * 1000),
        is_encrypted: true,
        expiration_date: messageExpiration !== 'never' ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) : null,
        message_type: 'text',
        read_status: true
      },
      {
        id: 2,
        content: "Parfait ! Pouvez-vous me donner plus de détails sur le type d'événement et le budget prévu ?",
        sender_id: currentUser?.id,
        created_at: new Date(Date.now() - 1 * 60 * 60 * 1000),
        is_encrypted: true,
        expiration_date: messageExpiration !== 'never' ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) : null,
        message_type: 'text',
        read_status: true
      },
      {
        id: 3,
  content: "C'est un concert privé pour 200 personnes. Budget entre 2000-3000€. Vous êtes disponible le 15 octobre ?",
        sender_id: conversation?.participants?.[0]?.user_profiles?.id,
        created_at: new Date(Date.now() - 30 * 60 * 1000),
        is_encrypted: true,
        expiration_date: messageExpiration !== 'never' ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) : null,
        message_type: 'text',
        read_status: false
      }
    ];
    setMessages(mockMessages);
  };

  const setupMessageEncryption = () => {
    // Mock encryption setup
    setEncryptionStatus(encryptionLevel === 'high' ? 'active' : 'standard');
  };

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!newMessage?.trim()) return;

    const message = {
      id: Date.now(),
      content: newMessage?.trim(),
      sender_id: currentUser?.id,
      created_at: new Date(),
      is_encrypted: true,
      expiration_date: messageExpiration !== 'never' ? new Date(Date.now() + getExpirationTime()) : null,
      message_type: 'text',
      read_status: false
    };

    setMessages(prev => [...prev, message]);
    setNewMessage('');
    scrollToBottom();

    // Setup message expiration timer
    if (messageExpiration !== 'never') {
      setupExpirationTimer(message);
    }
  };

  const getExpirationTime = () => {
    const times = {
      '1hour': 60 * 60 * 1000,
      '24hours': 24 * 60 * 60 * 1000,
      '7days': 7 * 24 * 60 * 60 * 1000,
      '30days': 30 * 24 * 60 * 60 * 1000
    };
    return times?.[messageExpiration] || times?.['7days'];
  };

  const setupExpirationTimer = (message) => {
    if (message?.expiration_date) {
      const timeUntilExpiry = new Date(message?.expiration_date) - new Date();
      setTimeout(() => {
        setMessages(prev => prev?.filter(m => m?.id !== message?.id));
      }, timeUntilExpiry);
    }
  };

  const handleFileSelect = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      // Mock secure file sharing
      setSelectedFile({
        name: file?.name,
        size: file?.size,
        type: file?.type,
        encrypted: true
      });
    }
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeUntilExpiration = (expirationDate) => {
    if (!expirationDate) return null;
    const now = new Date();
    const expiry = new Date(expirationDate);
    const diff = expiry - now;
    
    if (diff <= 0) return 'Expiré';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}j`;
    return `${hours}h`;
  };

  return (
  <div className="flex flex-col h-full bg-white dark:bg-neutral-900/40 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-neutral-900/30">
      {/* Thread Header */}
  <div className="bg-white dark:bg-neutral-900/60 border-b border-gray-200 dark:border-neutral-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <img
                src={conversation?.participants?.[0]?.user_profiles?.avatar_url || '/api/placeholder/40/40'}
                alt=""
                className="w-10 h-10 rounded-full"
              />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-neutral-100">
                {conversation?.participants?.[0]?.user_profiles?.full_name}
              </h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 dark:text-neutral-400">
                  {conversation?.participants?.[0]?.user_profiles?.partners?.[0]?.business_name}
                </span>
                {conversation?.participants?.[0]?.user_profiles?.partners?.[0]?.is_verified && (
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 bg-green-50 dark:bg-green-900/30 px-3 py-1 rounded-full">
              <Shield className="w-3 h-3 text-green-600" />
              <span className="text-xs text-green-600 font-medium">Chiffré E2E</span>
            </div>
            <Button variant="ghost" size="sm">
              <Phone className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <Video className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onStartBooking}
            >
              <Calendar className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Security Notice */}
  <div className="mt-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <Lock className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-800 dark:text-blue-300">
              Cette conversation est sécurisée. Vos coordonnées personnelles restent privées jusqu&apos;à acceptation mutuelle.
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
  <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages?.map((message) => {
          const isOwn = message?.sender_id === currentUser?.id;
          const timeUntilExpiry = getTimeUntilExpiration(message?.expiration_date);
          
          return (
            <div key={message?.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-1' : 'order-2'}`}>
                <div className={`relative px-4 py-2 rounded-lg ${
                  isOwn
                    ? 'bg-blue-500 text-white' :'bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-neutral-100'
                }`}>
                  <p className="text-sm">{message?.content?.replace("d'", "d&apos;")}</p>
                  
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-1">
                      <span className="text-xs opacity-70">
                        {formatTime(message?.created_at)}
                      </span>
                      {message?.is_encrypted && (
                        <Lock className="w-3 h-3 opacity-70" />
                      )}
                    </div>
                    
                    {timeUntilExpiry && timeUntilExpiry !== 'Expiré' && (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3 opacity-70" />
                        <span className="text-xs opacity-70">{timeUntilExpiry}</span>
                      </div>
                    )}
                  </div>

                  {/* Encryption Indicator */}
                  {message?.is_encrypted && (
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <Shield className="w-2 h-2 text-white" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-neutral-800 rounded-lg px-4 py-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Booking Quick Actions */}
  <div className="px-6 py-3 bg-gray-50 dark:bg-neutral-900/50 border-t border-gray-200 dark:border-neutral-800">
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={onStartBooking}
          >
            <Calendar className="w-4 h-4 mr-1" />
            Booking
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {/* TODO: share contract flow */}}
          >
            <FileText className="w-4 h-4 mr-1" />
            Contrat
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {/* TODO: request verification flow */}}
          >
            <Star className="w-4 h-4 mr-1" />
            Évaluation
          </Button>
        </div>
      </div>

      {/* Message Input */}
  <div className="bg-white dark:bg-neutral-900/60 border-t border-gray-200 dark:border-neutral-800 px-6 py-4">
        {selectedFile && (
          <div className="mb-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800 dark:text-blue-300">{selectedFile?.name}</span>
                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-1 rounded">
                  Chiffré
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedFile(null)}
              >
                ×
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center space-x-3">
          <input
            type="file"
            id="file-upload"
            className="hidden"
            onChange={handleFileSelect}
          />
          <label htmlFor="file-upload">
            <Button variant="ghost" size="sm" asChild>
              <span className="cursor-pointer">
                <Paperclip className="w-4 h-4" />
              </span>
            </Button>
          </label>
          
          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e?.target?.value)}
              onKeyPress={(e) => {
                if (e?.key === 'Enter') {
                  e?.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Message sécurisé et chiffré..."
              className="w-full px-4 py-3 border border-gray-300 dark:border-neutral-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12 bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 placeholder:text-gray-400 dark:placeholder:text-neutral-500"
            />
            <Button 
              variant="ghost" 
              size="sm"
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <Smile className="w-4 h-4" />
            </Button>
          </div>
          
          <Button
            onClick={sendMessage}
            disabled={!newMessage?.trim()}
            className="px-4 py-3"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>

        {/* Expiration Notice */}
        {messageExpiration !== 'never' && (
          <div className="mt-2 flex items-center justify-center">
            <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-neutral-500">
              <Clock className="w-3 h-3" />
              <span>Messages disparaissent dans {messageExpiration === '1hour' ? '1 heure' : messageExpiration === '24hours' ? '24 heures' : messageExpiration === '7days' ? '7 jours' : '30 jours'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecureMessageThread;